import React, { useState, useContext, useMemo, useCallback, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { NetworkStatus } from 'apollo-client';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';

import type { BannerStateContainer } from '@confluence/banners';
import { ContentTypesHeader } from '@confluence/content-types-header';
import type { ContentTypeShellProps } from '@confluence/content-types-bridge';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import {
	DATABASE_EXPERIENCE,
	DATABASE_CONTENT_EXPERIENCE,
	ExperienceAttributes,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { ContentViewedEvent } from '@confluence/content-body';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { DATABASE_VIEW } from '@confluence/named-routes';
import { NotFoundContained } from '@confluence/content-prerequisites';
import { useSessionData } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import {
	contentBoardStatusService,
	contentMoreActionsService,
} from '@confluence/content-types-bridge';
import type { ContentStatus, MoreActions } from '@confluence/content-types-header';
import { useThemingService } from '@confluence/whiteboard-common';

import { DatabaseOnboarding } from './DatabaseOnboarding';
import { MobileWebView } from './components/DatabaseMobileView';
import { DATABASE_IFRAME_TITLE } from './database-constants';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT, StartDatabasePageLoad } from './perf.config';
import { databaseShellStyle, databaseHeightContainer } from './database-shell-styles';
import { useDatabaseHeaderActions, useOnboardingService, usePostMessageHandler } from './hooks';
import { useDatabaseParams } from './database-params';
import type { DatabaseOnboardingService } from './services/database-onboarding-service';
import { useInitialUrl } from './hooks/useInitialUrl';

declare global {
	interface Window {
		databaseBridge?: {
			onboardingService: DatabaseOnboardingService;
			updateBoardStatus: (status: ContentStatus) => void;
			updateMoreActions: (items: MoreActions) => void;
			/*
			 * A hook for the database iframe to handle when database body loads successfully.
			 */
			onContentLoadSucceeded: (attributes: ExperienceAttributes) => void;
			/**
			 * A hook for the database iframe to handle when database body loads unsuccessfully.
			 */
			onContentLoadFailed: (error: Error, attributes: ExperienceAttributes) => void;
		};
	}
}

type DatabaseShellProps = ContentTypeShellProps & {
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	identity?: {
		accountId?: string | null;
		nickname?: string | null;
		picture?: string | null;
		email?: string | null;
		locale?: string | null;
	};
};

export const DatabaseShell = ({
	spaceKey,
	contentId,
	contentReferenceId,
	loading,
	queryNetworkStatus,
	spaceId,
	spaceName,
	title,
	identity,
}: DatabaseShellProps) => {
	const [headerHeight, setHeaderHeight] = useState<number>(0);

	const isSSR = Boolean(process.env.REACT_SSR);
	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	const isMobile = shouldShowMobileWeb();
	const contentActions = useDatabaseHeaderActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const onboardingService = useOnboardingService();
	const { activationId, userId, cloudId } = useSessionData();
	const { initialThemeSerialized, themingService } = useThemingService();
	const isThemePassingEnabled = fg('confluence_frontend_databases-theme-pass-enabled');

	const params = useDatabaseParams();
	const version = params['databases-frontend'];
	const isSmartLinkEmbed = params['isSmartLinkEmbed'];

	const initialUrl = useInitialUrl({
		version,
		isSmartLinkEmbed,
		initialTheme: isThemePassingEnabled ? initialThemeSerialized : undefined,
	});

	const updateDatabaseHeaderEvents: UIAnalyticsEventHandler = useCallback(
		(event: UIAnalyticsEvent) => {
			const newData = {
				objectType: 'database',
			};

			const newAttributes = {
				contentType: 'database',
			};

			event.update((payload) => {
				const mergedAttributes = {
					...payload.data?.attributes,
					...newAttributes,
				};
				return {
					...payload,
					data: {
						...payload.data,
						...newData,
						attributes: mergedAttributes,
					},
				};
			});
		},
		[],
	);

	const showContentTypesHeader =
		!isSSR && !isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	const showContentTypesHeaderPlaceholder =
		isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	const bridgeData = useMemo(
		() => ({
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId,
			spaceName,
			title,
			identity,
		}),
		[
			spaceKey,
			contentId,
			cloudId,
			activationId,
			contentReferenceId,
			userId,
			spaceId,
			spaceName,
			title,
			identity,
		],
	);

	const bridgeMethods = useMemo(
		() => ({
			onContentLoadSucceeded: (attributes: ExperienceAttributes) => {
				experienceTracker.succeed({
					name: DATABASE_CONTENT_EXPERIENCE,
					attributes,
				});
				DATABASE_VIEW_PAGE_CONTENT_SEGMENT.stop();
			},
			onContentLoadFailed: (error: Error, attributes: ExperienceAttributes) => {
				error.message = `Canvas Database error: ${error.message}`;

				experienceTracker.stopOnError({
					name: DATABASE_CONTENT_EXPERIENCE,
					attributes,
					error,
				});
				DATABASE_VIEW_PAGE_CONTENT_SEGMENT.cancel();
			},
			updateBoardStatus: (status: ContentStatus) => {
				contentBoardStatusService.updateActions(status);
			},
			updateMoreActions: (items: MoreActions) => {
				contentMoreActionsService.updateActions(items);
			},
			onboardingService,
			onThemeChange: (callback: () => void) => {
				themingService.onThemeChange(callback);
			},
		}),
		[experienceTracker, onboardingService, themingService],
	);

	usePostMessageHandler(iframeRef);

	const onIframeRef = useCallback(
		(iframe: HTMLIFrameElement | null) => {
			if (iframe) {
				iframeRef.current = iframe;
				iframe.contentWindow!.databaseBridge = bridgeMethods;

				iframe.contentWindow!.postMessage(
					{
						type: 'bridge-ready',
					},
					'*',
				);
			}
		},
		[bridgeMethods],
	);

	const onIframeLoaded = useCallback(
		(e) => {
			const iframe = e.target as HTMLIFrameElement;
			onIframeRef(iframe);
		},
		[onIframeRef],
	);

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: DATABASE_EXPERIENCE,
			error: new Error('DatabaseUnifiedQuery did not return valid referenceId'),
		});
		return <NotFoundContained route={DATABASE_VIEW} spaceKey={spaceKey} />;
	}

	return (
		<>
			<DatabaseOnboarding onboardingService={onboardingService} />
			<StartDatabasePageLoad contentId={contentId} />
			<Subscribe to={databaseHeightContainer}>
				{(bannerState: BannerStateContainer) => (
					<>
						{showContentTypesHeaderPlaceholder && <ContentTypesHeaderPlaceholder />}
						{showContentTypesHeader && (
							<AnalyticsListener onEvent={updateDatabaseHeaderEvents}>
								<ContentTypesHeader
									contentId={contentId}
									contentActions={contentActions}
									onHeightChange={setHeaderHeight}
								/>
							</AnalyticsListener>
						)}

						{isMobile ? (
							<MobileWebView bannerState={bannerState} />
						) : (
							<>
								<PageSegmentLoadStart metric={DATABASE_VIEW_PAGE_CONTENT_SEGMENT} />
								<iframe
									src={initialUrl}
									title={DATABASE_IFRAME_TITLE}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									style={databaseShellStyle(
										!isSmartLinkEmbed ? bannerState.getTotalHeight(true) + headerHeight : 0,
									)}
									data-bridge-data={!loading ? JSON.stringify(bridgeData) : undefined}
									ref={onIframeRef}
									onLoad={onIframeLoaded}
								/>
								{!isSSR && <ContentViewedEvent contentId={contentId} />}
							</>
						)}
					</>
				)}
			</Subscribe>
		</>
	);
};
